import 'quill-mention/autoregister';
import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  type JSX,
} from 'react';
import ReactQuill from 'react-quill-new';

import FieldLabel from '../fieldLabel/FieldLabel';

import 'quill-mention/dist/quill.mention.css';
import 'react-quill-new/dist/quill.snow.css';
import './TextareaFieldMentionFormatter.sass';

export enum ToolbarColourVariant {
  PRIMARY = 'primary',
  SECONDARY = 'secondary',
  WHITE = 'white',
}
type Props = {
  description?: string | JSX.Element;
  isDisabled?: boolean;
  isRequired?: boolean;
  label?: string | JSX.Element;
  name: string;
  onChange?: (value: { [key: string]: string }) => void;
  placeholder?: string;
  value?: any;
  changeTimeoutMs?: number;
  onBlur?: () => void;
  onChangeTimeout?: () => void;
  mentions?: { id: number; value: string }[];
  className?: string;
};

const TextareaFieldMentionFormatter: FC<React.PropsWithChildren<Props>> = ({
  description,
  isDisabled = false,
  isRequired = false,
  label,
  name,
  onChange,
  placeholder,
  value,
  onBlur,
  onChangeTimeout,
  changeTimeoutMs = 1000,
  mentions,
  className,
}: React.PropsWithChildren<Props>): JSX.Element => {
  const changeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    undefined
  );

  useEffect(() => {
    if (document) {
      // update the link tooltip placeholder from quill website
      document
        ?.querySelector('.ql-tooltip input')
        ?.setAttribute('data-link', 'https://payaca.com');
    }
  }, []);

  const modules = useMemo(() => {
    return {
      toolbar: [
        [
          'bold',
          'italic',
          'underline',
          'strike',
          { list: 'ordered' },
          { list: 'bullet' },
          'link',
        ],
      ],
      mention: {
        allowedChars: /^[A-Za-z\s]*$/,
        mentionDenotationChars: ['@'],
        source: function (
          searchTerm: string,
          renderItem: any,
          mentionChar: '@'
        ) {
          let values: any;
          if (mentionChar === '@') {
            values = mentions;
          }
          if (searchTerm.length === 0) {
            renderItem(values, mentionChar);
          } else {
            const matches = [];
            for (let i = 0; i < values?.length; i++)
              if (
                ~values[i].value.toLowerCase().indexOf(searchTerm.toLowerCase())
              )
                matches.push(values[i]);
            renderItem(matches, mentionChar);
          }
        },
      },
    };
  // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useMemo has a missing dependency: 'mentions'
  }, []); // re-render causes crash, so only set once on first render

  const onEditorChange = useCallback(
    (...args: any[]) => {
      const [
        // this value is wrong, quill v5 issue
        value,
        delta,
        source,
        // the correct value we want to editor.getSemanticHTML()
        editor,
      ] = args;

      const correctValue = editor.getSemanticHTML();

      onChange?.({
        [name]: correctValue,
      });

      if (changeTimeoutRef.current) {
        clearTimeout(changeTimeoutRef.current);

        changeTimeoutRef.current = setTimeout(() => {
          onChangeTimeout?.();
        }, changeTimeoutMs);
      }
    },
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useCallback has an unnecessary dependency: changeTimeoutRef.
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useCallback has a missing dependency: 'name'
    [onChange, onChangeTimeout, changeTimeoutRef.current, changeTimeoutMs]
  );

  return (
    <div
      className={`text-area-field-mention-formatter${
        className ? ` ${className}` : ''
      }`}
    >
      <FieldLabel
        label={label}
        isRequired={isRequired}
        description={description}
      />
      <ReactQuill
        theme="snow"
        bounds={`[data-text-editor="name"]`}
        // quill must be in an uncontrolled state
        // https://www.npmjs.com/package/react-quill-new#usage
        defaultValue={value}
        modules={modules}
        readOnly={isDisabled}
        placeholder={placeholder}
        onChange={onEditorChange}
        onBlur={() => onBlur?.()}
      />
    </div>
  );
};

export default TextareaFieldMentionFormatter;
